/**
 * Bildzuschnitt per Canvas (wiederverwendbar, 2026-09-17).
 * Pure Helfer sind separat testbar; getCroppedBlob braucht ein Browser-Canvas.
 */
export interface PixelArea { x: number; y: number; width: number; height: number }

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Größe des umschließenden Rechtecks eines um `rotation` Grad gedrehten Bildes (pure). */
export function rotatedSize(width: number, height: number, rotation: number): { width: number; height: number } {
  const r = toRad(rotation);
  return {
    width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
    height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
  };
}

/** Zielgröße mit Obergrenze bei gleichem Seitenverhältnis (pure). */
export function fitOutputSize(width: number, height: number, maxSize: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxSize) return { width: Math.round(width), height: Math.round(height) };
  const f = maxSize / longest;
  return { width: Math.round(width * f), height: Math.round(height * f) };
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
    img.src = src;
  });

/**
 * Schneidet `area` (Pixel im ungedrehten Originalbild, wie von react-easy-crop geliefert)
 * aus dem um `rotation` Grad gedrehten Bild aus und liefert ein JPEG-Blob.
 */
export async function getCroppedBlob(
  imageSrc: string,
  area: PixelArea,
  rotation = 0,
  { maxSize = 1600, mimeType = 'image/jpeg', quality = 0.92 } = {},
): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const { width: bw, height: bh } = rotatedSize(image.width, image.height, rotation);

  // 1) Gedrehtes Vollbild auf eine Zwischen-Leinwand zeichnen
  const stage = document.createElement('canvas');
  stage.width = Math.round(bw);
  stage.height = Math.round(bh);
  const sctx = stage.getContext('2d');
  if (!sctx) throw new Error('Canvas nicht verfügbar');
  sctx.translate(bw / 2, bh / 2);
  sctx.rotate(toRad(rotation));
  sctx.translate(-image.width / 2, -image.height / 2);
  sctx.drawImage(image, 0, 0);

  // 2) Ausschnitt in die Zielgröße übertragen
  const out = fitOutputSize(area.width, area.height, maxSize);
  const canvas = document.createElement('canvas');
  canvas.width = out.width;
  canvas.height = out.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas nicht verfügbar');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(stage, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Zuschnitt fehlgeschlagen'))), mimeType, quality);
  });
}

/**
 * Blob → Base64. Standard: ROHES Base64 ohne „data:…;base64,“-Prefix (so erwartet es /api/file).
 * Mit { dataUrl: true } kommt die vollständige Data-URL (z. B. für <img src>).
 */
export const blobToBase64 = (blob: Blob, { dataUrl = false } = {}): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      if (dataUrl) return resolve(result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.substring(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
